import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../lib/colors';
import { MediumText, RegularText, DemiLightText } from '../../lib/Text';
import { getItemWithExpiration } from '../../lib/utils';
import { isAdminAlertMockEnabled } from '../../api/adminAlerts';
import {
    useAdminAlertsQuery,
    useMarkAdminAlertReadMutation,
} from '../../services/useAdminAlertsService';
import { AdminAlert } from '../../types/adminAlert';
import AlertCard from './AlertCard';
import { buildNoShowScheduleRoute, buildSessionReportSupportRoute } from './utils';
import { usePayrollAuthContext } from '../../context/PayrollAuthContext';
import { usePayrollAuth } from '../../hook/usePayrollAuth';
import { resolveLockSectionFromPath } from '../../lib/lockedSections';

interface AlertCenterPanelProps {
    onClose: () => void;
    anchorRef: React.RefObject<HTMLElement>;
}

const resolveRoute = (alert: AdminAlert): { pathname: string; search?: string; state?: unknown } => {
    switch (alert.type) {
        case 'NEW_OPPORTUNITY':
            return {
                pathname: '/centerReport/newOpportunities',
                search: alert.metadata?.opportunityId
                    ? `?opportunityId=${alert.metadata.opportunityId}`
                    : undefined,
            };
        case 'KIOSK_TRAINER_PROMO_LEAD':
            return {
                pathname: '/member/communication',
                search: alert.metadata?.commBoxId
                    ? `?commBoxId=${alert.metadata.commBoxId}`
                    : undefined,
            };
        case 'COMM_BOX_NEW':
        case 'COMM_BOX_REPLY':
            return {
                pathname: '/member/communication',
                search: alert.metadata?.commBoxId
                    ? `?commBoxId=${alert.metadata.commBoxId}`
                    : undefined,
            };
        case 'PAYMENT_FAIL':
            return {
                pathname: alert.metadata?.memberId
                    ? `/member/${alert.metadata.memberId}`
                    : '/memberList',
            };
        case 'BAD_REVIEW':
            return {
                pathname: '/centerReport/memberReviews',
                search: alert.metadata?.reviewId ? `?reviewId=${alert.metadata.reviewId}` : undefined,
            };
        case 'SCHEDULE_NO_SHOW':
            return buildNoShowScheduleRoute(alert);
        case 'SESSION_REPORT_SUPPORT_RESOLVED':
            return buildSessionReportSupportRoute(alert);
        default:
            return { pathname: '/dash' };
    }
};

const AlertCenterPanel: React.FC<AlertCenterPanelProps> = ({ onClose, anchorRef }) => {
    const navigate = useNavigate();
    const panelRef = useRef<HTMLDivElement>(null);
    const accessToken = getItemWithExpiration('authorization') ?? '';
    const mockEnabled = isAdminAlertMockEnabled();
    const { isSectionLocked, isSectionUnlocked } = usePayrollAuthContext();
    usePayrollAuth();

    const { data, isFetching, isError, error, refetch } = useAdminAlertsQuery({ accessToken, limit: 20 });
    const markRead = useMarkAdminAlertReadMutation(accessToken);
    const getAlertLockSection = (alert: AdminAlert) => {
        const route = resolveRoute(alert);
        return resolveLockSectionFromPath(route.pathname, route.search);
    };
    const isAlertLocked = (alert: AdminAlert) => {
        if (alert.type !== 'BAD_REVIEW') return false;

        const lockSection = getAlertLockSection(alert);
        return isSectionLocked(lockSection) && !isSectionUnlocked(lockSection);
    };

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (
                panelRef.current &&
                !panelRef.current.contains(e.target as Node) &&
                anchorRef.current &&
                !anchorRef.current.contains(e.target as Node)
            ) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [anchorRef, onClose]);

    const handleCardClick = (alert: AdminAlert) => {
        if (!alert.isRead) {
            markRead.mutate(alert.id);
        }
        const route = resolveRoute(alert);
        navigate({ pathname: route.pathname, search: route.search }, route.state ? { state: route.state } : undefined);
        onClose();
    };

    const handleMarkAllRead = () => {
        if (markRead.isLoading) return;
        visibleItems.filter((a) => !a.isRead).forEach((alert) => {
            markRead.mutate(alert.id);
        });
    };

    const items = data?.items ?? [];
    const visibleItems = items.filter((alert) => !isAlertLocked(alert));
    const hasUnread = visibleItems.some((a) => !a.isRead);

    return (
        <>
            <div
                ref={panelRef}
                className="absolute top-full right-0 mt-[12px] w-[380px] flex flex-col overflow-hidden rounded-[20px] border border-[#E2E8F0] z-50"
                style={{
                    background: 'rgba(255,255,255,0.98)',
                    boxShadow: '0 24px 48px rgba(15,23,42,0.14)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    maxHeight: 'min(640px, 80vh)',
                }}
            >
            {/* Header */}
            <div className="flex items-center justify-between px-[20px] pt-[18px] pb-[12px]">
                <div className="flex items-center gap-[8px]">
                    <MediumText fontSize={15} lineHeight={20} color={colors.Text_1}>
                        알림센터
                    </MediumText>
                    {mockEnabled && (
                        <span
                            className="px-[6px] py-[1px] rounded-[6px]"
                            style={{
                                backgroundColor: '#FEF3C7',
                                color: '#92400E',
                                fontSize: 10,
                                fontWeight: 600,
                            }}
                        >
                            MOCK
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleMarkAllRead}
                    disabled={!hasUnread || markRead.isLoading}
                    className="text-[12px] transition-colors"
                    style={{
                        color: hasUnread ? colors.primary : colors.LAVEL_4,
                        cursor: hasUnread ? 'pointer' : 'default',
                    }}
                >
                    모두 읽음 처리
                </button>
            </div>

            <div className="h-[1px] bg-[#EEF2F7]" />

            {/* List */}
            <div className="overflow-y-auto flex-1">
                {!accessToken && (
                    <div className="px-[20px] py-[48px] text-center">
                        <RegularText fontSize={13} lineHeight={18} color={colors.LAVEL_4}>
                            로그인 후 이용해주세요
                        </RegularText>
                    </div>
                )}

                {accessToken && isFetching && !data && (
                    <div className="px-[20px] py-[48px] text-center">
                        <RegularText fontSize={13} lineHeight={18} color={colors.LAVEL_4}>
                            불러오는 중...
                        </RegularText>
                    </div>
                )}

                {accessToken && !isFetching && isError && (
                    <div className="px-[20px] py-[48px] text-center flex flex-col items-center gap-[12px]">
                        <RegularText fontSize={13} lineHeight={18} color={colors.LAVEL_4}>
                            알림을 불러오지 못했어요
                        </RegularText>
                        {error instanceof Error && (
                            <DemiLightText fontSize={11} lineHeight={15} color={colors.LAVEL_4}>
                                {error.message}
                            </DemiLightText>
                        )}
                        <button
                            type="button"
                            onClick={() => refetch()}
                            className="px-[12px] py-[6px] rounded-[10px] border border-[#E2E8F0]"
                        >
                            <DemiLightText fontSize={12} lineHeight={17} color={colors.Text_1}>
                                다시 시도
                            </DemiLightText>
                        </button>
                    </div>
                )}

                {accessToken && !isError && !isFetching && visibleItems.length === 0 && (
                    <div className="px-[20px] py-[48px] text-center">
                        <RegularText fontSize={13} lineHeight={18} color={colors.LAVEL_4}>
                            새 알림이 없어요
                        </RegularText>
                    </div>
                )}

                {accessToken && !isError && visibleItems.length > 0 && (
                    <div className="flex flex-col">
                        {visibleItems.map((alert, idx) => (
                            <React.Fragment key={alert.id}>
                                <AlertCard alert={alert} onClick={handleCardClick} />
                                {idx < visibleItems.length - 1 && (
                                    <div className="h-[1px] bg-[#EEF2F7] mx-[16px]" />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>
            </div>

        </>
    );
};

export default AlertCenterPanel;
